import axiosInstance from "../../utils/axiosConfig";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TailSpin } from "react-loader-spinner";

import { DataTable } from "../../components/data-table";
import { API_URL } from "../../constant";
import { useAuth } from "../../context/auth-context";
import {
  buildMerchantRows,
  createMerchantColumns,
  MERCHANTS_TABLE_LIMIT,
  type MerchantRecord,
  type MerchantRow,
} from "./merchantsTable";

const Merchants = () => {
  const { isAuthenticated, query } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(1);
  const [merchants, setMerchants] = useState<MerchantRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalMerchants, setTotalMerchants] = useState<number>(0);

  useEffect(() => {
    setPage(1);
  }, [query]);

  useEffect(() => {
    if (!isAuthenticated) return;
    setLoading(true);

    const fetchMerchants = () => {
      const searchParam = query ? `&search=${query}` : "";
      axiosInstance
        .get(
          `${API_URL}api/users/merchants/?limit=${MERCHANTS_TABLE_LIMIT}&page=${page}${searchParam}`
        )
        .then((r: any) => {
          const businesses = Array.isArray(r?.data?.data?.businesses)
            ? [...r.data.data.businesses].sort(
                (a: any, b: any) =>
                  new Date(b.createdAt).getTime() -
                  new Date(a.createdAt).getTime()
              )
            : [];
          setTotalPages(Math.max(1, r.data.data.meta.totalPages ?? 1));
          setTotalMerchants(r.data.data.meta.totalUsers ?? 0);
          setMerchants(businesses);
        })
        .catch((e: any) => {
          console.log(e?.response?.data?.message || "An error occurred");
        })
        .finally(() => setLoading(false));
    };

    const debounceTimer = setTimeout(fetchMerchants, 600);
    return () => clearTimeout(debounceTimer);
  }, [page, isAuthenticated, query]);

  const openMerchant = useCallback(
    (row: MerchantRow) => {
      const businessId = row.raw?._id;
      if (!businessId) return;
      // The record rides along so the detail page's Info tab renders instantly;
      // it refetches from transactions if the state is lost on refresh.
      navigate(`/merchants/${businessId}`, { state: { merchant: row.raw } });
    },
    [navigate]
  );

  const columns = useMemo(
    () => createMerchantColumns(openMerchant),
    [openMerchant]
  );
  const rows = useMemo<MerchantRow[]>(
    () => buildMerchantRows(merchants),
    [merchants]
  );

  if (loading && merchants.length === 0) {
    return (
      <div className="flex justify-center rounded-xl border border-[#2C363E] bg-[#161E25]/40 py-16">
        <TailSpin color="#0B75C2" height={36} width={36} />
      </div>
    );
  }

  return (
    <div className="w-full pb-10">
      {loading && (
        <p className="mb-2 text-[12px] text-[#869AA9]">Updating list…</p>
      )}
      <DataTable<MerchantRow>
        columns={columns}
        data={rows}
        currentPage={page}
        setCurrentPage={setPage}
        totalPage={totalPages}
        count={totalMerchants}
        limit={MERCHANTS_TABLE_LIMIT}
        onRowClick={openMerchant}
        emptyContent="No merchants to display."
        className="overflow-hidden rounded-xl border border-[#2C363E]"
        fixedBodyHeight="min(560px, 55vh)"
      />
    </div>
  );
};

export default Merchants;
